import { useParams, useNavigate, Link } from 'react-router-dom';
import { Table, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiClipboardDocument } from 'react-icons/hi2';
import { PageContent } from '@/components/layout/PageLayout';
import DetailHeader from '@/components/layout/DetailHeader';
import { Section, SectionTitle } from '@/components/layout/Toolbar.styled';
import { ClickableTr } from '@/styles/shared.styled';
import { DefinitionList } from '@/components/common/DefinitionList';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useGetCourtDetailQuery } from '@/store/courtsApi';

const TYPE_BADGE_VARIANT: Record<string, string> = {
  SadNajwyzszy: 'dark',
  NaczelnySadAdministracyjny: 'dark',
  WojewodzkiSadAdministracyjny: 'info',
  Apelacyjny: 'danger',
  Okregowy: 'warning',
  Rejonowy: 'primary',
  WojskowyOkregowy: 'success',
  WojskowyGarnizonowy: 'success',
  TrybunalKonstytucyjny: 'dark',
  TrybunalStanu: 'dark',
};

function CopyButton({ text }: { text: string }) {
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
  };
  return (
    <button
      className="btn btn-sm btn-outline-secondary ms-2 py-0 px-1"
      onClick={handleCopy}
      title="Kopiuj"
    >
      <HiClipboardDocument size="0.85rem" />
    </button>
  );
}

export default function CourtDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: court, isLoading } = useGetCourtDetailQuery(id!, { skip: !id });

  if (isLoading || !court) {
    return <PageContent><LoadingSpinner /></PageContent>;
  }

  return (
    <PageContent>
      <DetailHeader
        onBack={() => navigate(-1)}
        title={court.name}
        meta={
          <>
            <Badge bg={TYPE_BADGE_VARIANT[court.type] ?? 'secondary'}>
              {t(`courtType.${court.type}`)}
            </Badge>
            <span>{court.cityName}</span>
          </>
        }
      />

      <Section>
        <SectionTitle>{t('courts.detail.info')}</SectionTitle>
        <DefinitionList
          items={[
            { label: t('courts.table.address'), value: court.address },
            { label: t('courts.detail.phone'), value: court.phone },
            { label: t('courts.detail.email'), value: court.email },
            {
              label: t('courts.table.oplataSkarbowa'),
              value: court.oplataSkarbowaNrKonta && (
                <>
                  <span className="font-monospace">{court.oplataSkarbowaNrKonta}</span>
                  <CopyButton text={court.oplataSkarbowaNrKonta} />
                  {court.oplataSkarbowaNrKontaUrl && (
                    <><br /><a href={court.oplataSkarbowaNrKontaUrl} target="_blank" rel="noopener noreferrer" className="small">
                      {t('courts.detail.checkLink')} — {court.oplataSkarbowaNrKontaUrl}
                    </a></>
                  )}
                  {court.oplataSkarbowaNazwaOdbiorcy && (
                    <><br /><small className="text-muted">{court.oplataSkarbowaNazwaOdbiorcy}</small></>
                  )}
                </>
              ),
            },
          ]}
        />
      </Section>

      {(court.parentCourt || court.childCourts.length > 0) && (
        <Section>
          <SectionTitle>{t('courts.detail.hierarchy')}</SectionTitle>
          <DefinitionList
            items={[
              {
                label: t('courts.detail.parentCourt'),
                value: court.parentCourt && (
                  <Link to={`/courts/${court.parentCourt.id}`}>{court.parentCourt.name}</Link>
                ),
              },
              {
                label: t('courts.detail.childCourts'),
                value: court.childCourts.length > 0 && (
                  <>
                    {court.childCourts.map((child) => (
                      <div key={child.id}>
                        <Link to={`/courts/${child.id}`}>{child.name}</Link>
                      </div>
                    ))}
                  </>
                ),
              },
            ]}
          />
        </Section>
      )}

      {court.bankAccounts.length > 0 && (
        <Section>
          <SectionTitle>{t('courts.detail.bankAccounts')} ({court.bankAccounts.length})</SectionTitle>
          {court.bankAccountsUrl && (
            <p className="mb-3">
              <a href={court.bankAccountsUrl} target="_blank" rel="noopener noreferrer" className="small">
                {t('courts.detail.checkLink')} — {court.bankAccountsUrl}
              </a>
            </p>
          )}
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>{t('courts.detail.accountType')}</th>
                <th>{t('courts.detail.accountNumber')}</th>
                <th>{t('courts.detail.accountDescription')}</th>
              </tr>
            </thead>
            <tbody>
              {court.bankAccounts.map((ba) => (
                <tr key={ba.id}>
                  <td>{t(`courtBankAccountType.${ba.accountType}`, ba.accountType)}</td>
                  <td>
                    <span className="font-monospace">{ba.accountNumber}</span>
                    <CopyButton text={ba.accountNumber} />
                  </td>
                  <td><small className="text-muted">{ba.description ?? '—'}</small></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      )}

      <Section>
        <SectionTitle>{t('courts.detail.sprawy')} ({court.sprawy.length})</SectionTitle>
        {!court.sprawy.length ? (
          <p className="text-muted">{t('courts.detail.noSprawy')}</p>
        ) : (
          <Table hover responsive size="sm">
            <thead>
              <tr>
                <th>{t('sprawy.table.signature')}</th>
                <th>{t('sprawy.form.subject')}</th>
                <th>{t('sprawy.table.portalStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {court.sprawy.map((s) => (
                <ClickableTr key={s.id} onClick={() => navigate(`/sprawy/${s.id}`)}>
                  <td className="fw-semibold"><Link to={`/sprawy/${s.id}`}>{s.signature}</Link></td>
                  <td>{s.subject ?? '—'}</td>
                  <td><Badge bg="secondary">{t(`sprawaStatus.${s.portalStatus}`)}</Badge></td>
                </ClickableTr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </PageContent>
  );
}
